import { LocalStorageService } from 'angular-2-local-storage';
import { LocationService } from './../../../services/location.service';
import { Timeline } from './../../../interfaces/timeline';
import { Project } from './../../../interfaces/project';
import { MediaclipService } from './../../../services/mediaclip.service';
import { Mediaclip } from './../../../interfaces/mediaclip';
import { SapiService } from './../../../services/sapi.service';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BbSelectOption } from './../bb-select/bb-select.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

@Component({
	selector: 'bb-preview-page',
	templateUrl: './bb-preview-page.component.html',
	styleUrls: ['./bb-preview-page.component.scss']
})
export class BbPreviewPageComponent implements OnInit, OnDestroy {
	routeParams: {
		type?: string;
		clipId?: string;
		interactivityId?: string;
		listId?: string;
		projectId?: string;
		projectContext?: string;
		playout?: string;
		versionState?: string;
		templateId?: string;
	} = { type: 'c', versionState: 'autosave' };
	typeOptions = [{
		label: this.translate.instant('PROJECT'),
		value: 'p'
	}, {
		label: this.translate.instant('MEDIACLIP'),
		value: 'c'
	}, {
		label: this.translate.instant('PLAYLIST'),
		value: 'l'
	}];
	projectContextOptions: BbSelectOption[] = [];
	iframeSrc: SafeResourceUrl;
	mediaclip: Mediaclip;
	timeline: Timeline;
	project: Project;
	template: any;
	collapsed = false;
	lastPlayoutPreviewCodeKey: string;

	subscriptions: Subscription = new Subscription();

	constructor(
		private translate: TranslateService,
		public locationService: LocationService,
		private domSanitizer: DomSanitizer,
		private sapiService: SapiService,
		private mediaclipService: MediaclipService,
		private router: Router,
		private localStorageService: LocalStorageService,
		private route: ActivatedRoute
	) {
		const win = window as any;
		if (!win.updateOvpPreviewWin) {
			win.updateOvpPreviewWin = (fromPreview) => {
				if (!fromPreview) {
					this.updateRoute();
				}
				this.update(true);
			};
		}
	}

	ngOnInit() {
		this.subscriptions.add(this.route.queryParams.subscribe(params => {
			this.routeParams = { ...this.routeParams, ...params };
			this.update();
		}));
		this.lastPlayoutPreviewCodeKey = 'lastPlayoutPreviewCode' + this.localStorageService.get('ovp-currentPublicationId');

		this.updateRoute();
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onToggleClick() {
		this.collapsed = !this.collapsed;
	}

	onTypeChange() {
		this.updateRoute();
	}

	onClipChange() {
		this.routeParams.type = 'c';
		this.updateRoute();
	}

	fetchTimeline() {
		if (this.routeParams.interactivityId) {
			this.sapiService.get('timeline', this.routeParams.interactivityId, '', { state: this.routeParams.versionState }).subscribe((timeline) => {
				this.timeline = timeline;
			});
		}
	}

	fetchProject(id) {
		this.sapiService.get('project', id).subscribe((p) => {
			const project = p as Project;
			this.project = project as Project;
			this.projectContextOptions = project?.contexts?.map(c => (
				{
					label: c.label,
					value: c.code
				}
			)) || [];
		});
	}

	fetchClip() {
		if (!this.routeParams.clipId) {
			this.mediaclip = null;
			return;
		}
		this.sapiService.get('commonclip', this.routeParams.clipId).subscribe((clip) => {
			this.mediaclip = clip;
			if (this.mediaclip.timelines?.[0]?.id) {
				this.routeParams.interactivityId = this.mediaclip.timelines[0].id;
			} else {
				delete this.routeParams.interactivityId;
			}
			this.getClipProjectRelation(this.mediaclip);
			this.update();
		});
	}

	fetchTemplate() {
		if (!this.routeParams.templateId) {
			this.template = null;
			return;
		}
		this.sapiService.get('app', this.routeParams.templateId).subscribe((template) => {
			this.template = template;
			if (this.template.timelines?.[0]?.id) {
				this.routeParams.interactivityId = this.template.timelines[0].id;
			} else {
				delete this.routeParams.interactivityId;
			}
		});
	}

	onPlayoutChange() {
		if (this.routeParams.playout) {
			this.localStorageService.set(this.lastPlayoutPreviewCodeKey, this.routeParams.playout);
		}
		this.updateRoute();
	}

	onProjectChange() {
		this.routeParams.type = 'p';
		this.updateRoute();
	}

	onContextChange() {
		this.updateRoute();
	}

	onRefreshClick() {
		this.update(true);
	}

	onCloseClick() {
		this.collapsed = true;
	}

	updateRoute() {
		this.router.navigate([],
			{
				relativeTo: this.route,
				queryParams: this.routeParams,
				queryParamsHandling: 'merge', // remove to replace all query params by provided
			}
		);
	}

	update(updateTimeline = false) {
		if (this.routeParams.projectId && this.project?.id !== this.routeParams.projectId) {
			this.fetchProject(this.routeParams.projectId);
		}
		if (!this.routeParams.templateId && this.routeParams.clipId && this.mediaclip?.id !== this.routeParams.clipId) {
			this.fetchClip();
		}
		if (this.routeParams.templateId && this.template?.id !== this.routeParams.templateId) {
			this.fetchTemplate();
		}
		if (updateTimeline || this.routeParams.interactivityId && this.timeline?.id !== this.routeParams.interactivityId) {
			this.fetchTimeline();
		}
		this.setIframeSrc();
	}

	setIframeSrc() {
		const id = this.routeParams.type === 'p' && this.routeParams.projectId
			|| this.routeParams.type === 'c' && this.routeParams.clipId
			|| this.routeParams.type === 'l' && this.routeParams.listId;
		if (!this.routeParams.playout) {
			this.routeParams.playout = this.localStorageService.get(this.lastPlayoutPreviewCodeKey) || 'default';
		}

		const src = `${this.locationService.server}/p/${this.routeParams.playout}/${this.routeParams.type}/${id
		}.html?inheritDimensions=true&fitmode=FIT_BOTH&iframeBreakout=false&useSapi=true&state=${this.routeParams.versionState || 'autosave'
		}&useSession=true&noStats=true&placementOption=default&context=${this.routeParams.projectContext || ''
		}${this.routeParams.templateId ? ('&timelineId=' + this.routeParams.interactivityId) : ''}&ts=${Date.now()}`;
		this.iframeSrc = this.domSanitizer.bypassSecurityTrustResourceUrl(src);
	}

	private getClipProjectRelation(clip) {
		const relations = this.mediaclipService.getRelations(clip);
		if (relations?.project?.length) {
			this.routeParams.projectId = relations.project[0];
			this.fetchProject(this.routeParams.projectId);
		} else {
			this.projectContextOptions = [];
			this.project = null;
			delete this.routeParams.projectId;
		}
	}
}
